import { useRef } from 'react'
import { formatSpeedMph, windCardinal } from './format'
import { civilParts } from './storyTime'
import { PLUME_PAINT } from './geo'
import type { DisplayUnits } from './types'

type WindCompassProps = {
  fromDeg: number
  disabled?: boolean
  onChange: (fromDeg: number) => void
}

function degFromPoint(clientX: number, clientY: number, el: Element): number {
  const box = el.getBoundingClientRect()
  const dx = clientX - (box.left + box.width / 2)
  const dy = clientY - (box.top + box.height / 2)
  return (Math.atan2(dx, -dy) * (180 / Math.PI) + 360) % 360
}

export function WindCompass({ fromDeg, disabled, onChange }: WindCompassProps) {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const dragRef = useRef(false)

  function apply(clientX: number, clientY: number): void {
    const el = svgRef.current
    if (!el || disabled) return
    onChange(Math.round(degFromPoint(clientX, clientY, el)) % 360)
  }

  const rad = (fromDeg * Math.PI) / 180
  const x = 70 + 48 * Math.sin(rad)
  const y = 70 - 48 * Math.cos(rad)
  const tx = 70 - 42 * Math.sin(rad)
  const ty = 70 + 42 * Math.cos(rad)
  const hx = 70 + 54 * Math.sin(rad)
  const hy = 70 - 54 * Math.cos(rad)
  const left = 6
  const nx = hx - left * Math.cos(rad)
  const ny = hy - left * Math.sin(rad)
  const px = hx + left * Math.cos(rad)
  const py = hy + left * Math.sin(rad)

  return (
    <svg
      ref={svgRef}
      className={`sc-compass${disabled ? ' off' : ''}`}
      width="140"
      height="140"
      viewBox="0 0 140 140"
      role="slider"
      aria-label="Wind from direction"
      aria-valuemin={0}
      aria-valuemax={359}
      aria-valuenow={fromDeg}
      aria-disabled={disabled || undefined}
      onPointerDown={(e) => {
        if (disabled) return
        dragRef.current = true
        e.currentTarget.setPointerCapture(e.pointerId)
        apply(e.clientX, e.clientY)
      }}
      onPointerMove={(e) => {
        if (!dragRef.current) return
        apply(e.clientX, e.clientY)
      }}
      onPointerUp={() => {
        dragRef.current = false
      }}
      onPointerCancel={() => {
        dragRef.current = false
      }}
    >
      <circle cx="70" cy="70" r="64" fill="none" stroke="#4a4a54" strokeWidth="1" />
      <circle cx="70" cy="70" r="56" fill="#32323a" stroke="#3e3e48" strokeWidth="0.5" />
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i * 30 * Math.PI) / 180
        const inner = i % 3 === 0 ? 46 : 50
        return (
          <line
            key={i}
            x1={70 + inner * Math.sin(a)}
            y1={70 - inner * Math.cos(a)}
            x2={70 + 56 * Math.sin(a)}
            y2={70 - 56 * Math.cos(a)}
            stroke="#6a6a74"
            strokeWidth={i % 3 === 0 ? 1.4 : 0.8}
          />
        )
      })}
      <text x="70" y="11" textAnchor="middle" fontSize="13" fontWeight="600" fill="#F0706E">
        N
      </text>
      <text x="131" y="74" textAnchor="middle" fontSize="12" fontWeight="500" fill="#d4d0c8">
        E
      </text>
      <text x="70" y="135" textAnchor="middle" fontSize="12" fontWeight="500" fill="#d4d0c8">
        S
      </text>
      <text x="9" y="74" textAnchor="middle" fontSize="12" fontWeight="500" fill="#d4d0c8">
        W
      </text>
      <line x1="70" y1="70" x2={tx} y2={ty} stroke="#FAC775" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.4" />
      <line x1="70" y1="70" x2={x} y2={y} stroke="#FAC775" strokeWidth="2.5" strokeLinecap="round" />
      <polygon points={`${hx},${hy} ${nx},${ny} ${px},${py}`} fill="#FAC775" />
      <circle cx="70" cy="70" r="4" fill="#FAC775" />
    </svg>
  )
}

/** Analog face for the scenario playhead. */
export function WindAnalogClock({
  playheadIso,
  timeZone,
}: {
  playheadIso: string
  timeZone: string
}) {
  const parts = civilParts(playheadIso, timeZone)
  const hours = parts.hour % 12
  const minutes = parts.minute
  const seconds = parts.second
  const hourAngle = (hours + minutes / 60) * 30
  const minuteAngle = (minutes + seconds / 60) * 6
  const secondAngle = seconds * 6

  function hand(angleDeg: number, length: number, width: number, color: string) {
    const rad = (angleDeg * Math.PI) / 180
    return (
      <line
        x1={70}
        y1={70}
        x2={70 + length * Math.sin(rad)}
        y2={70 - length * Math.cos(rad)}
        stroke={color}
        strokeWidth={width}
        strokeLinecap="round"
      />
    )
  }

  return (
    <svg
      className="sc-wind-clock"
      width="140"
      height="140"
      viewBox="0 0 140 140"
      role="img"
      aria-label="Session clock"
    >
      <circle cx="70" cy="70" r="64" fill="none" stroke="#4a4a54" strokeWidth="1" />
      <circle cx="70" cy="70" r="56" fill="#32323a" stroke="#3e3e48" strokeWidth="0.5" />
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i * 30 * Math.PI) / 180
        return (
          <line
            key={i}
            x1={70 + 48 * Math.sin(a)}
            y1={70 - 48 * Math.cos(a)}
            x2={70 + 56 * Math.sin(a)}
            y2={70 - 56 * Math.cos(a)}
            stroke={i % 3 === 0 ? '#9a9aa4' : '#5a5a64'}
            strokeWidth={i % 3 === 0 ? 1.6 : 0.8}
          />
        )
      })}
      {hand(hourAngle, 28, 3.2, '#c8c8ce')}
      {hand(minuteAngle, 42, 2.2, '#e4e4ea')}
      {hand(secondAngle, 48, 1.2, '#FAC775')}
      <circle cx="70" cy="70" r="3.5" fill="#FAC775" />
    </svg>
  )
}

/** Compact Control Bar wind chip: compass + plume wedge in one glyph, then numbers. */
export function StripWindBadge({
  fromDeg,
  speedMph,
  plumeDistanceM,
  displayUnits = 'imperial',
  title,
  muted = false,
  hint = false,
  onClick,
}: {
  fromDeg: number
  speedMph: number
  plumeDistanceM: number
  displayUnits?: DisplayUnits
  title?: string
  muted?: boolean
  /** Quiet emphasis while a surface plume is under the cursor. */
  hint?: boolean
  onClick?: () => void
}) {
  const rad = (fromDeg * Math.PI) / 180
  const cx = 11
  const cy = 11
  const tipX = cx + 7.5 * Math.sin(rad)
  const tipY = cy - 7.5 * Math.cos(rad)
  const tailX = cx - 5 * Math.sin(rad)
  const tailY = cy + 5 * Math.cos(rad)
  // Plume length bands — wedge radius inside the compass disc (meters).
  const band = plumeDistanceM < 200 ? 'sm' : plumeDistanceM < 600 ? 'md' : 'lg'
  const wedgeR = band === 'sm' ? 5.5 : band === 'md' ? 7.5 : 9.2
  const half = 0.38
  const down = rad + Math.PI
  const a0 = down - half
  const a1 = down + half
  const wx = (a: number) => cx + wedgeR * Math.sin(a)
  const wy = (a: number) => cy - wedgeR * Math.cos(a)
  const wedge = `M ${cx} ${cy} L ${wx(a0)} ${wy(a0)} A ${wedgeR} ${wedgeR} 0 0 1 ${wx(a1)} ${wy(a1)} Z`
  const cardinal = windCardinal(fromDeg)

  return (
    <button
      type="button"
      className={`sc-strip-wind${muted ? ' muted' : ''}${hint ? ' hint' : ''}`}
      title={title ?? 'Inspect surface wind'}
      onClick={onClick}
    >
      <svg
        className={`sc-strip-wind-glyph ${band}`}
        width="28"
        height="28"
        viewBox="0 0 22 22"
        aria-hidden="true"
      >
        {/* Soft disc — same idea as plume sitting over a footprint on the map. */}
        <circle cx={cx} cy={cy} r="9.5" fill="#3a3a44" stroke="#5a5a62" strokeWidth="1" />
        <path
          d={wedge}
          fill={`${PLUME_PAINT.surface.fill}55`}
          stroke={PLUME_PAINT.surface.line}
          strokeWidth="1"
        />
        <line
          x1={tailX}
          y1={tailY}
          x2={tipX}
          y2={tipY}
          stroke="#FAC775"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r="1.6" fill="#FAC775" />
      </svg>
      <span className="sc-strip-wind-nums">
        {Math.round(fromDeg)}° {cardinal} · {formatSpeedMph(speedMph, displayUnits)}
      </span>
    </button>
  )
}

